// Same-origin model routes, each pinned to immutable weights and a journal root.
export interface ModelEndpoint {
  id: string;
  label: string;
  sha256: string;
}

export interface ModelDirectory {
  models: ModelEndpoint[];
  defaultModelId: string;
  sessionModels: Record<string, string>;
}

export function parseModelDirectory(text: string | null): ModelDirectory {
  if (text === null || text.trim() === "" || text.trim() === "[]")
    return { models: [], defaultModelId: "", sessionModels: {} };
  if (text.length > 131072) throw new Error("Model directory is too large");
  const value = JSON.parse(text);
  if (value?.schemaVersion !== "mutago-model-directory-v1" || !Array.isArray(value.models) ||
      value.models.length < 1 || value.models.length > 256) throw new Error("Invalid model directory");
  const ids = new Set<string>();
  const models = value.models.map((entry: unknown) => {
    if (entry === null || typeof entry !== "object") throw new Error("Invalid model endpoint");
    const row = entry as Record<string, unknown>;
    if (Object.keys(row).sort().join(",") !== "id,label,sha256" ||
        typeof row.id !== "string" || row.id.trim() !== row.id || !/^[a-zA-Z0-9_-]{1,64}$/.test(row.id) ||
        typeof row.label !== "string" || row.label.trim() === "" || row.label.length > 80 ||
        typeof row.sha256 !== "string" || row.sha256.length !== 64 || !/^[0-9a-f]{64}$/.test(row.sha256) ||
        ids.has(row.id)) throw new Error("Invalid or duplicate model endpoint");
    ids.add(row.id);
    return { id: row.id, label: row.label, sha256: row.sha256 };
  });
  if (!ids.has(value.defaultModelId) || !value.sessionModels || typeof value.sessionModels !== "object" ||
      Array.isArray(value.sessionModels) || Object.keys(value.sessionModels).length > 64 ||
      !Object.values(value.sessionModels).every((id) => typeof id === "string" && ids.has(id)))
    throw new Error("Invalid model or session binding");
  return { models, defaultModelId: value.defaultModelId, sessionModels: value.sessionModels };
}

export function selectedModelId(directory: ModelDirectory, url: URL, sessionId: string): string {
  const explicit = url.searchParams.get("model");
  const owner = Object.hasOwn(directory.sessionModels, sessionId) ? directory.sessionModels[sessionId] : undefined;
  const id = explicit ?? owner ?? directory.defaultModelId;
  if (directory.models.length && (!directory.models.some((row) => row.id === id) || (owner && owner !== id)))
    throw new Error("Unknown model or mismatched session model");
  return id;
}

export function modelSessionUrl(current: string, model: ModelEndpoint, sessionId: string): string {
  if (sessionId.length !== 34 || !/^s-[0-9a-f]{32}$/.test(sessionId)) throw new Error("Fresh session identity required");
  const url = new URL(current);
  if (url.protocol !== "http:" && url.protocol !== "https:") throw new Error("Unsupported page protocol");
  url.pathname = "/";
  url.search = "";
  url.hash = "";
  url.searchParams.set("session", sessionId);
  url.searchParams.set("model", model.id);
  return url.toString();
}
